<template>
    <v-container fluid class="external-organizer-page">
        <v-row justify="center">
            <v-col cols="12" md="10" lg="8">
                <v-card class="external-organizer-card" elevation="2">
                    <v-toolbar color="primary" dark flat>
                        <div class="d-flex align-center">
                            <div class="logo-box mr-3">
                                <img src="/images/logo-icon.png" alt="MIP" />
                            </div>
                            <div>
                                <div class="toolbar-title">External Organizer Registration</div>
                                <div class="toolbar-subtitle">MIP Online Membership</div>
                            </div>
                        </div>

                        <v-spacer></v-spacer>

                        <v-btn small light :loading="loading" @click="loadData">
                            Refresh
                        </v-btn>
                    </v-toolbar>

                    <v-card-text class="pa-5">
                        <v-alert v-if="error" type="error" dense text>
                            {{ error }}
                        </v-alert>

                        <v-alert v-if="success" type="success" dense text>
                            {{ success }}
                        </v-alert>

                        <v-skeleton-loader
                            v-if="loading"
                            type="article, actions"
                        ></v-skeleton-loader>

                        <template v-else-if="record">
                            <div class="record-summary mb-4">
                                <div class="font-weight-bold">
                                    Secure Code: {{ securecode || '-' }}
                                </div>
                                <div>
                                    Status:
                                    <v-chip x-small dark :color="statusColor">
                                        {{ statusText }}
                                    </v-chip>
                                </div>
                            </div>

                            <v-alert dense text type="info" class="mb-4">
                                Please complete the event/program details and upload the supporting document or payment proof if requested.
                            </v-alert>

                            <v-form ref="form" v-model="valid" lazy-validation>
                                <v-row dense>
                                    <v-col cols="12" md="6">
                                        <v-text-field
                                            v-model="form.eoorgname"
                                            label="Organizer Name"
                                            :rules="[rules.required]"
                                        ></v-text-field>
                                    </v-col>

                                    <v-col cols="12" md="6">
                                        <v-text-field
                                            v-model="form.eoname"
                                            label="Event / Program Name"
                                            :rules="[rules.required]"
                                        ></v-text-field>
                                    </v-col>

                                    <v-col cols="12" md="6">
                                        <v-text-field
                                            v-model="form.eoemail"
                                            label="Organizer Email"
                                            :rules="[rules.required, rules.email]"
                                        ></v-text-field>
                                    </v-col>

                                    <v-col cols="12" md="6">
                                        <v-text-field
                                            v-model="form.eocontactno"
                                            label="Organizer Contact No."
                                            :rules="[rules.required]"
                                        ></v-text-field>
                                    </v-col>

                                    <v-col cols="12" md="6">
                                        <v-text-field
                                            v-model="form.epdate"
                                            label="Event / Program Date"
                                            type="date"
                                            :rules="[rules.required]"
                                        ></v-text-field>
                                    </v-col>

                                    <v-col cols="12" md="6">
                                        <v-text-field
                                            v-model="form.epduration"
                                            label="Event / Program Duration"
                                            type="number"
                                            min="1"
                                            suffix="day(s)"
                                            :rules="[rules.required]"
                                        ></v-text-field>
                                    </v-col>

                                    <v-col cols="12" md="6">
                                        <v-file-input
                                            v-model="form.epposter"
                                            label="Poster / Brochure"
                                            accept=".jpg,.jpeg,.png,.pdf"
                                            show-size
                                            prepend-icon="mdi-paperclip"
                                        ></v-file-input>

                                        <a
                                            v-if="record.epposter"
                                            :href="buildFileUrl(record.epposter)"
                                            target="_blank"
                                            rel="noopener"
                                        >
                                            View current poster / brochure
                                        </a>
                                    </v-col>

                                    <v-col cols="12" md="6">
                                        <v-file-input
                                            v-model="form.eppayproof"
                                            label="Payment Proof"
                                            accept=".jpg,.jpeg,.png,.pdf"
                                            show-size
                                            prepend-icon="mdi-paperclip"
                                        ></v-file-input>

                                        <a
                                            v-if="record.eppayproof"
                                            :href="buildFileUrl(record.eppayproof)"
                                            target="_blank"
                                            rel="noopener"
                                        >
                                            View current payment proof
                                        </a>
                                    </v-col>
                                </v-row>

                                <div class="actions">
                                    <v-btn
                                        color="primary"
                                        :loading="saving"
                                        :disabled="saving"
                                        @click="submitForm"
                                    >
                                        Submit Registration
                                    </v-btn>
                                </div>
                            </v-form>
                        </template>

                        <v-alert v-else type="warning" dense text>
                            Invalid or expired external organizer registration link.
                        </v-alert>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
const axios = require('axios')

export default {
    name: 'MOPExternalOrganizer',

    props: {
        securelink: {
            type: String,
            default: '',
        },
        securecode: {
            type: String,
            default: '',
        },
        url: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            loading: false,
            saving: false,
            valid: true,
            record: null,
            error: '',
            success: '',
            form: {
                id: '',
                eoname: '',
                eoemail: '',
                eoorgname: '',
                eocontactno: '',
                epdate: '',
                epduration: '',
                epposter: null,
                eppayproof: null,
            },
            rules: {
                required: value => !!value || 'This information is required',
                email: value => {
                    if (!value) {
                        return true
                    }

                    return /.+@.+\..+/.test(value) || 'E-mail must be valid'
                },
            },
        }
    },

    computed: {
        statusText() {
            const status = String(this.record && this.record.epstatus ? this.record.epstatus : '01')
            const labels = {
                '01': 'Pending Submission',
                '02': 'Pending Payment / Review',
                '03': 'Completed',
            }

            return labels[status] || 'Pending Submission'
        },

        statusColor() {
            const status = String(this.record && this.record.epstatus ? this.record.epstatus : '01')
            const colors = {
                '01': 'warning',
                '02': 'primary',
                '03': 'success',
            }

            return colors[status] || 'warning'
        },
    },

    mounted() {
        this.loadData()
    },

    methods: {
        parseResponse(data) {
            if (typeof data !== 'string') {
                return data
            }

            try {
                return JSON.parse(data)
            } catch (error) {
                return data
            }
        },

        formatDateForInput(value) {
            if (!value) {
                return ''
            }

            const date = new Date(value)
            if (Number.isNaN(date.getTime())) {
                return String(value).substring(0, 10)
            }

            const month = `${date.getMonth() + 1}`.padStart(2, '0')
            const day = `${date.getDate()}`.padStart(2, '0')

            return `${date.getFullYear()}-${month}-${day}`
        },

        buildFileUrl(path) {
            if (!path) {
                return '#'
            }

            if (/^https?:\/\//i.test(path)) {
                return path
            }

            const baseUrl = this.url || window.location.origin

            return `${baseUrl.replace(/\/$/, '')}/${String(path).replace(/^\//, '')}`
        },

        fillForm(record) {
            this.form.id = record.id || ''
            this.form.eoname = record.eoname || ''
            this.form.eoemail = record.eoemail || ''
            this.form.eoorgname = record.eoorgname || ''
            this.form.eocontactno = record.eocontactno || ''
            this.form.epdate = this.formatDateForInput(record.epdate)
            this.form.epduration = record.epduration || ''
            this.form.epposter = null
            this.form.eppayproof = null
        },

        async loadData() {
            this.loading = true
            this.error = ''
            this.success = ''

            try {
                const response = await axios.post('/cpd/ext-org-form/getdata', {
                    securelink: this.securelink,
                    securecode: this.securecode,
                })
                const result = this.parseResponse(response.data)

                if (!result || result.status !== 'success' || !result.data) {
                    this.record = null
                    this.error = result && result.message ? result.message : 'Unable to load external organizer registration.'
                    return
                }

                this.record = result.data
                this.fillForm(result.data)
            } catch (error) {
                this.record = null
                this.error = 'Unable to load external organizer registration. Please try again.'
            } finally {
                this.loading = false
            }
        },

        async submitForm() {
            this.error = ''
            this.success = ''

            if (!this.$refs.form.validate()) {
                return
            }

            this.saving = true

            try {
                const formData = new FormData()
                formData.append('securelink', this.securelink)
                formData.append('securecode', this.securecode)
                formData.append('id', this.form.id)
                formData.append('eoname', this.form.eoname)
                formData.append('eoemail', this.form.eoemail)
                formData.append('eoorgname', this.form.eoorgname)
                formData.append('eocontactno', this.form.eocontactno)
                formData.append('epdate', this.form.epdate)
                formData.append('epduration', this.form.epduration)

                if (this.form.epposter) {
                    formData.append('epposter', this.form.epposter)
                }

                if (this.form.eppayproof) {
                    formData.append('eppayproof', this.form.eppayproof)
                }

                const response = await axios.post('/cpd/ext-org-form/update', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                })
                const result = this.parseResponse(response.data)

                if (!result || result.status !== 'success') {
                    this.error = result && result.message ? result.message : 'Unable to submit registration.'
                    return
                }

                this.success = 'Registration details have been submitted successfully.'
                await this.loadData()
            } catch (error) {
                this.error = 'Unable to submit registration. Please check the form and try again.'
            } finally {
                this.saving = false
            }
        },
    },
}
</script>

<style scoped>
.external-organizer-page {
    min-height: 100vh;
    background: #f3f6fa;
    padding-top: 48px;
    padding-bottom: 48px;
}

.external-organizer-card {
    border-radius: 4px;
    overflow: hidden;
}

.logo-box {
    width: 42px;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    border-radius: 3px;
}

.logo-box img {
    width: 34px;
    height: 34px;
    object-fit: contain;
}

.toolbar-title {
    font-size: 18px;
    line-height: 22px;
    font-weight: 600;
}

.toolbar-subtitle {
    font-size: 12px;
    line-height: 16px;
    opacity: 0.9;
}

.record-summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 14px 16px;
    background: #eaf4ff;
    border-radius: 3px;
}

.actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 18px;
}

@media (max-width: 600px) {
    .external-organizer-page {
        padding-top: 16px;
        padding-bottom: 16px;
    }

    .record-summary {
        align-items: flex-start;
        flex-direction: column;
    }
}
</style>
